import { Duration } from 'luxon';
import { exponentialBackoffFullJitter, retryUntilNoException, } from '@lucidlink/tscore';
import { LockingError, } from '#types/sdk';
export { DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT } from '#types/sdk';
export const createExternalFilesManager = (logger, externalFilesManager) => {
    return new ExternalFilesManagerImpl(logger, externalFilesManager);
};
class ExternalFilesManagerImpl {
    logger;
    externalFilesManager;
    extFiles;
    extDataStores;
    constructor(logger, externalFilesManager) {
        this.logger = logger;
        this.externalFilesManager = externalFilesManager;
        this.extFiles = new ExternalFilesImpl(this.externalFilesManager.files());
        this.extDataStores = new DataStoresImpl(this.logger, this.externalFilesManager.dataStores());
    }
    files() {
        return this.extFiles;
    }
    dataStores() {
        return this.extDataStores;
    }
}
class ExternalFilesImpl {
    files;
    constructor(files) {
        this.files = files;
    }
    link(path, file) {
        return this.files.link(path, file);
    }
    updateHttpFileUrl(path, url) {
        return this.files.updateHttpFileUrl(path, url);
    }
}
// NOTE(LUCID-28929): the methods below forward S3 credentials to the WebFs
// WASM module. If a backend response (LucidAPI/WebService) ever echoes credential
// material back in an error body, FetchError.buildMessage will JSON.stringify
// it into error.message and surface it via devtools console / Sentry. No
// in-scope endpoint does that today; sanitize FetchError.details (or strip
// credentials from the request before throwing here) if that changes.
class DataStoresImpl {
    logger;
    dataStores;
    constructor(logger, dataStores) {
        this.logger = logger;
        this.dataStores = dataStores;
    }
    add(name, dataStore) {
        return retryUntilNoException(() => this.dataStores.add(name, dataStore), {
            retries: MAX_RETRIES,
            sleep: retryBackoff,
            onError: handleLockingError,
            onRetry: buildRetryLogger(this.logger, `add data store "${name}"`),
        });
    }
    remove(name) {
        return retryUntilNoException(() => this.dataStores.remove(name), {
            retries: MAX_RETRIES,
            sleep: retryBackoff,
            onError: handleLockingError,
            onRetry: buildRetryLogger(this.logger, `remove data store "${name}"`),
        });
    }
    list() {
        return this.dataStores.list();
    }
    stats() {
        return this.dataStores.stats();
    }
    async listExternalFiles(dataStoreName) {
        const iterator = await this.dataStores.listExternalFiles(dataStoreName);
        return toExternalFileEntryIterator(iterator);
    }
    async listExternalFilesPaginated(dataStoreName, params) {
        const result = await this.dataStores.listExternalFilesPaginated(dataStoreName, params);
        return {
            items: result.items.map(toExternalFileEntry),
            nextCursor: result.nextCursor,
        };
    }
    initializeRekey(name, credentials) {
        return this.dataStores.initializeRekey(name, credentials);
    }
    listObjects(name, params) {
        return this.dataStores.listObjects(name, params);
    }
}
function toExternalFileEntry(entry) {
    return { id: entry.linkId, inodeId: entry.inodeId, type: entry.type };
}
function toExternalFileEntryIterator(iterator) {
    return {
        [Symbol.asyncIterator]() {
            return this;
        },
        async next() {
            const result = await iterator.next();
            return result.done
                ? result
                : { done: false, value: toExternalFileEntry(result.value) };
        },
    };
}
const MAX_RETRIES = 10;
const retryBackoff = exponentialBackoffFullJitter({
    delay: Duration.fromObject({ seconds: 1 }).toMillis(),
    maxDelay: Duration.fromObject({ minutes: 1 }).toMillis(),
});
const handleLockingError = (error) => error instanceof LockingError ? 'retry' : 'abort';
const buildRetryLogger = (logger, operation) => ({ error, delayMs }) => {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`Failed to ${operation}: ${message}, retrying after ${delayMs}ms.`);
};
